import React, {
  useCallback, useEffect, useMemo, useState
} from 'react'
import PropTypes from 'prop-types'
import qs from 'query-string'
import { useMutation, useQuery } from 'react-apollo'
import { useForm } from 'react-hook-form'
import { Redirect } from 'react-router-dom'

import ProfilesForm from '../components/profiles-form'
import { useConfig } from '../../../hooks/use-config'
import { useIsAuthenticatedAndRedirect } from '../../../hooks/use-is-authenticated-and-redirect'
import { useMutateAndLoading } from '../../../hooks/use-mutate-and-loading'
import { useRequirePin } from '../../../hooks/use-require-pin'
import { useProfileOptions } from '../hooks/use-profile-options'
import { getAccountProfileById } from '../../../lib/account'
import { getGQLErrorMsg } from '../../../lib/apollo'
import { getModalLocation } from '../../../lib/modal'
import { getDefaultParentalRating } from '../../../lib/parental-ratings'
import { segmentTrackEditProfile } from '../../../segment/segment-track'
import getGenderOptions from '../../../utils/getGenderOptions'
import { getProfileByName } from '../../../utils/getProfileByName'
import { MODALS } from '../../../constants'
import { LoadingModal } from '../../../components/loading/modal'
import ACCOUNT_QUERY from '../../../../graphql/queries/account.gql'
import PROFILE_MUTATION from '../../../../graphql/mutations/profile.gql'
import PROFILE_PREFERENCES_QUERY from '../../../../graphql/queries/profile-preferences.gql'
import PROFILE_PREFERENCES_MUTATION from '../../../../graphql/mutations/update-profile-preferences.gql'

/**
 * Displays a form for editing a user's profile.
 * Redirects the user to the profile management screen upon successful submission.
 */
function EditProfiles({ history, location }) {
  const selectedProfileId = qs.parse(location.search).profileId
  const [isAvatarSelected, setIsAvatarSelected] = useState(false)
  const [formError, setFormError] = useState('')
  const {
    isAuthenticated,
    shouldRedirect,
    redirectTo
  } = useIsAuthenticatedAndRedirect(location)
  const accountQuery = useQuery(ACCOUNT_QUERY, {
    notifyOnNetworkStatusChange: true,
    skip: !isAuthenticated
  })
  const profilePreferencesQuery = useQuery(PROFILE_PREFERENCES_QUERY, {
    fetchPolicy: 'cache-first',
    notifyOnNetworkStatusChange: true,
    skip: !isAuthenticated || !selectedProfileId,
    variables: { profileId: selectedProfileId }
  })
  const {
    appConfig,
    parentalRatingsData,
    ageGroupsData,
    configLoading,
    configError
  } = useConfig()
  const [mutateProfile] = useMutation(PROFILE_MUTATION, {
    update: (proxy, response) => {
      const accountData = proxy.readQuery({ query: ACCOUNT_QUERY })
      const profiles = response.data?.profile?.profiles || accountData.account.profiles

      proxy.writeQuery({
        query: ACCOUNT_QUERY,
        data: {
          ...accountData,
          account: {
            ...accountData.account,
            profiles
          }
        }
      })
    }
  })
  const [mutateProfilePreferences] = useMutation(PROFILE_PREFERENCES_MUTATION)
  const mutateAndLoading = useMutateAndLoading(mutateProfile)
  const account = accountQuery.data && accountQuery.data.account
  const selectedProfile = account &&
    getAccountProfileById(account, selectedProfileId)
  const profilePreferences = profilePreferencesQuery.data?.profilePreferences
  const {
    ageGroupOptions,
    birthYearOptions,
    parentalRatingOptions
  } = useProfileOptions({ ageGroupsData, appConfig, parentalRatingsData })
  const genderOptions = useMemo(() => getGenderOptions(), [])
  const selectedAgeGroup = ageGroupOptions.find(
    option => option.value.id === selectedProfile?.ageGroup
  )
  const defaultParentalRating = getDefaultParentalRating(parentalRatingsData || [])
  const parentalRating = parentalRatingOptions.find(
    option => option.value === selectedProfile?.maxRating
  ) || parentalRatingOptions.find(
    option => option.value === defaultParentalRating?.rating
  )
  const avatar = selectedProfile?.avatar || appConfig?.avatars?.find(
    profileAvatar => profileAvatar.isDefault
  )
  const defaultValues = useMemo(() => ({
    id: selectedProfile?.id,
    isKid: selectedProfile?.isKid || false,
    isDefault: selectedProfile?.isDefault || false,
    avatar,
    name: selectedProfile?.name || '',
    ageGroup: selectedAgeGroup,
    birthYear: birthYearOptions.find(
      option => option.value === selectedProfile?.birthYear
    ) || '',
    gender: genderOptions.find(
      option => option.value === selectedProfile?.gender
    ) || '',
    parentalRating,
    email: selectedProfile?.email || '',
    mobile: selectedProfile?.mobile?.trim() || '',
    nextEpisodeAutoplay: profilePreferences?.nextEpisodeAutoplayEnabled,
    trailerAutoplayOnBrowsePage:
      profilePreferences?.trailerAutoplayOnBrowsePageEnabled,
    trailerAutoplayOnShowPage:
      profilePreferences?.trailerAutoplayOnShowPageEnabled
  }), [
    avatar,
    birthYearOptions,
    genderOptions,
    parentalRating,
    profilePreferences,
    selectedAgeGroup,
    selectedProfile
  ])
  const {
    control,
    handleSubmit,
    setError,
    setValue,
    clearErrors,
    reset,
    formState: {
      errors,
      touchedFields,
      isSubmitted,
      isSubmitting
    }
  } = useForm({
    defaultValues,
    mode: 'onChange'
  })

  useRequirePin(account)

  useEffect(() => {
    if (
      !selectedProfile ||
      !appConfig?.avatars ||
      !parentalRatingsData ||
      !ageGroupsData
    ) {
      return
    }

    reset(defaultValues)
  }, [
    ageGroupsData,
    appConfig?.avatars,
    defaultValues,
    parentalRatingsData,
    reset,
    selectedProfile
  ])

  const onCancel = useCallback(() => {
    if (isAvatarSelected) {
      setIsAvatarSelected(false)
      return
    }

    if (location.state?.from) {
      history.goBack()
      return
    }

    history.push(getModalLocation(
      location,
      MODALS.qsParams.profilesManagement,
      null,
      { from: false }
    ))
  }, [history, isAvatarSelected, location])

  const onSubmit = async values => {
    setFormError('')
    clearErrors()

    try {
      const profileData = await mutateAndLoading('profilesEdit', {
        variables: {
          input: {
            id: values.id,
            isKid: values.isKid,
            isDefault: values.isDefault,
            name: values.name.trim(),
            email: values.isKid ? '' : values.email,
            avatar: values.avatar?.id,
            mobile: values.isKid ? '' : values.mobile.trim(),
            ageGroup: values.isKid ? values.ageGroup?.value.id : undefined,
            maxRating: values.parentalRating?.value || values.parentalRating?.rating,
            birthYear: values.birthYear?.value,
            gender: values.gender?.value
          }
        }
      })

      await mutateProfilePreferences({
        variables: {
          input: {
            profileId: values.id,
            trailerAutoplayOnShowPageEnabled:
              values.trailerAutoplayOnShowPage ?? false,
            trailerAutoplayOnBrowsePageEnabled:
              values.trailerAutoplayOnBrowsePage ?? false,
            nextEpisodeAutoplayEnabled: values.nextEpisodeAutoplay ?? false
          }
        }
      })

      const updatedProfile = getProfileByName({ data: profileData }, values.name)

      if (updatedProfile) {
        const {
          id, name, gender, birthYear, email, mobile, isKid, maxRating
        } = updatedProfile

        segmentTrackEditProfile({
          id,
          name,
          gender,
          birthYear,
          email,
          mobile,
          isKid,
          maxRating
        })
      }

      onCancel()
    } catch (submitError) {
      const graphQLErrors = submitError.graphQLErrors || []
      const nameError = graphQLErrors.find(
        graphQLError => graphQLError.code === 'PROFILE_NAME_DUPLICATE_ERROR'
      )
      const emailError = graphQLErrors.find(
        graphQLError => graphQLError.code === 'INVALID_INPUT' &&
          graphQLError.message.includes('email')
      )
      const mobileError = graphQLErrors.find(
        graphQLError => graphQLError.code === 'INVALID_INPUT' &&
          graphQLError.message.includes('mobile')
      )
      let fieldError

      if (nameError) {
        fieldError = { name: 'name', error: nameError }
      } else if (emailError) {
        fieldError = { name: 'email', error: emailError }
      } else if (mobileError) {
        fieldError = { name: 'mobile', error: mobileError }
      }

      if (fieldError) {
        setError(fieldError.name, {
          type: 'server',
          message: fieldError.error.message
        }, {
          shouldFocus: true
        })
        return
      }

      setFormError(getGQLErrorMsg(submitError))
    }
  }

  if (shouldRedirect) {
    return <Redirect to={redirectTo} />
  }

  if (
    accountQuery.loading ||
    accountQuery.error ||
    profilePreferencesQuery.loading ||
    profilePreferencesQuery.error ||
    configLoading ||
    configError ||
    !account ||
    !appConfig?.avatars ||
    !parentalRatingsData ||
    !ageGroupsData
  ) {
    return <LoadingModal />
  }

  if (!selectedProfile) {
    return (
      <Redirect
        to={getModalLocation(location, MODALS.qsParams.profilesManagement)}
      />
    )
  }

  return (
    <ProfilesForm
      ageGroupOptions={ageGroupOptions}
      avatars={appConfig.avatars}
      birthYearOptions={birthYearOptions}
      control={control}
      error={formError}
      errors={errors}
      handleSubmit={handleSubmit(onSubmit)}
      isAvatarSelected={isAvatarSelected}
      isSubmitted={isSubmitted}
      isSubmitting={isSubmitting}
      onCancel={onCancel}
      parentalRatingOptions={parentalRatingOptions}
      parentalRatingsData={parentalRatingsData}
      setIsAvatarSelected={setIsAvatarSelected}
      setValue={setValue}
      touchedFields={touchedFields}
      header="Edit Profile"
    />
  )
}

EditProfiles.propTypes = {
  history: PropTypes.shape({
    goBack: PropTypes.func.isRequired,
    push: PropTypes.func.isRequired
  }).isRequired,
  location: PropTypes.shape({
    pathname: PropTypes.string.isRequired,
    search: PropTypes.string.isRequired,
    state: PropTypes.shape({
      from: PropTypes.oneOfType([PropTypes.bool, PropTypes.string])
    })
  }).isRequired
}

export default EditProfiles
